import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Calendar, Clock, Loader2, CheckCircle2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AddToCalendarModal({ issue, token, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  
  // Form state
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    date: today,
    time: '09:00',
    isAllDay: false,
    duration_hours: 1,
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const eventData = {
        issue_id: issue.id,
        summary: `[#${issue.id}] ${issue.summary}`,
        description: formData.description || `Issue MantisBT #${issue.id}\n\nProgetto: ${issue.project?.name || 'N/A'}\nStato: ${issue.status?.name || 'N/A'}\nPriorità: ${issue.priority?.name || 'N/A'}`,
        date: formData.date,
        time: formData.isAllDay ? null : formData.time,
        duration_hours: formData.duration_hours
      };
      
      // Use admin endpoint with bearer token
      const response = await axios.post(
        `${BACKEND_URL}/api/google/calendar/event`,
        eventData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(response.data);
      if (onSuccess) onSuccess(response.data);
      
    } catch (err) {
      console.error('Calendar event error:', err);
      if (err.response?.status === 401) {
        setError('Google Calendar non connesso. Vai su Impostazioni > Google Calendar per connetterti.');
      } else {
        setError(err.response?.data?.detail || 'Errore nella creazione dell\'evento');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[rgb(39,39,42)]">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[rgb(59,130,246)]" />
            <h3 className="text-lg font-bold font-mono text-[rgb(250,250,250)]">
              Aggiungi al Calendario
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-[rgb(161,161,170)] hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="p-4">
          {success ? (
            <div className="text-center py-6">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h4 className="text-lg font-mono font-bold text-[rgb(250,250,250)] mb-2">
                Evento Creato!
              </h4>
              <p className="text-sm text-[rgb(161,161,170)] font-mono mb-4">
                L&apos;evento è stato aggiunto al tuo Google Calendar
              </p>
              {success.event_link && (
                <a
                  href={success.event_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[rgb(59,130,246)] hover:underline font-mono text-sm"
                >
                  <ExternalLink className="h-4 w-4" />
                  Apri in Google Calendar
                </a>
              )}
              <div className="mt-6">
                <Button onClick={onClose} className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono">
                  Chiudi
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Issue info */}
              <div className="bg-[rgb(39,39,42)]/50 rounded p-3">
                <p className="text-xs text-[rgb(161,161,170)] font-mono mb-1">Issue</p>
                <p className="text-sm text-[rgb(250,250,250)] font-mono">
                  #{issue.id} - {issue.summary?.substring(0, 60)}{issue.summary?.length > 60 ? '...' : ''}
                </p>
              </div>
              
              {/* Date */}
              <div>
                <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                  Data *
                </label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                  className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                />
              </div>
              
              {/* All day toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isAllDay"
                  checked={formData.isAllDay}
                  onChange={(e) => setFormData({...formData, isAllDay: e.target.checked})}
                  className="w-4 h-4 rounded border-[rgb(63,63,70)] bg-[rgb(39,39,42)]"
                />
                <label htmlFor="isAllDay" className="text-sm font-mono text-[rgb(161,161,170)]">
                  Tutto il giorno
                </label>
              </div>
              
              {/* Time & Duration (if not all day) */}
              {!formData.isAllDay && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                      <Clock className="h-3 w-3 inline mr-1" />
                      Ora
                    </label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                      Durata (ore)
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="8"
                      value={formData.duration_hours}
                      onChange={(e) => setFormData({...formData, duration_hours: parseInt(e.target.value) || 1})}
                      className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                    />
                  </div>
                </div>
              )}
              
              {/* Description */}
              <div>
                <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                  Note aggiuntive
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Descrizione opzionale..."
                  rows={3}
                  className="w-full bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono rounded-md p-2 text-sm placeholder:text-[rgb(161,161,170)]/50"
                />
              </div>
              
              {/* Error */}
              {error && (
                <div className="bg-red-500/10 border border-red-500 rounded p-3">
                  <p className="text-red-500 text-sm font-mono">{error}</p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  className="font-mono"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creazione...
                    </>
                  ) : (
                    <>
                      <Calendar className="h-4 w-4 mr-2" />
                      Crea Evento
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default AddToCalendarModal;
